import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  Timeline as TimelineIcon,
  People as PeopleIcon,
  TrendingDown as LeakIcon,
  School as GradIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, CohortSurvivalData } from '../../services/learningAnalyticsApi';

const CohortSurvivalTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CohortSurvivalData | null>(null);
  const [startYear, setStartYear] = useState<string>('');
  const [level, setLevel] = useState<string>('1'); // Default to Primary
  const [district, setDistrict] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (startYear) params.start_year = parseInt(startYear);
      if (level) params.level = parseInt(level);
      if (district) params.district = district;
      const response = await learningAnalyticsAPI.getCohortSurvival(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load cohort data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [startYear, level, district]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  if (!data) return null;

  const { summary, grade_survival, district_comparison } = data;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Cohort Start Year" value={startYear}
              onChange={(e) => setStartYear(e.target.value)}>
              <MenuItem value="">Default (3 years ago)</MenuItem>
              {[2025, 2024, 2023, 2022, 2021, 2020].map(y => (
                <MenuItem key={y} value={y}>{y} Cohort</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Education Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)} placeholder="All Districts" />
          </Grid>
        </Grid>
      </Paper>

      {/* Info Banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Tracking <strong>{data.cohort_size.toLocaleString()}</strong> students who started in{' '}
        <strong>{data.level}</strong> ({data.start_year} cohort) through {summary.years_tracked} years.
      </Alert>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PeopleIcon color="primary" />
                <Typography variant="body2" color="textSecondary">Starting Cohort</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {summary.starting_cohort?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TimelineIcon color="success" />
                <Typography variant="body2" color="textSecondary">Current Survivors</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {summary.current_survivors?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <GradIcon color="info" />
                <Typography variant="body2" color="textSecondary">Completion Rate</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold"
                color={summary.estimated_completion_rate >= 70 ? 'success.main' : 'warning.main'}>
                {summary.estimated_completion_rate || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LeakIcon color="error" />
                <Typography variant="body2" color="textSecondary">Leakiest Transition</Typography>
              </Box>
              {summary.leakiest_transition ? (
                <>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {summary.leakiest_transition.from} → {summary.leakiest_transition.to}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.leakiest_transition.students_lost.toLocaleString()} students lost
                  </Typography>
                </>
              ) : (
                <Typography variant="h6" color="textSecondary">-</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Survival Curve */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Cohort Survival Curve</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Percentage of original cohort still enrolled at each grade
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={grade_survival}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade_label" />
                  <YAxis domain={[0, 100]} unit="%" />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      const labels: Record<string, string> = {
                        survival_rate: 'Survival Rate',
                        on_track_rate: 'On-Track Rate',
                      };
                      return [`${value}%`, labels[name] || name];
                    }}
                  />
                  <Legend />
                  <ReferenceLine y={80} stroke="#388e3c" strokeDasharray="3 3" label="80% target" />
                  <Line type="monotone" dataKey="survival_rate" stroke="#1976d2"
                    strokeWidth={3} name="survival_rate" dot={{ r: 5 }} />
                  <Line type="monotone" dataKey="on_track_rate" stroke="#f57c00"
                    strokeWidth={2} strokeDasharray="5 5" name="on_track_rate" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Attrition per Grade */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Attrition by Grade</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={grade_survival.filter(g => g.attrition_this_year > 0)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade_label" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => value.toLocaleString()} />
                  <Bar dataKey="attrition_this_year" fill="#d32f2f" name="Students Lost" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grade Survival Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Detailed Grade-by-Grade Tracking</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Year</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell align="right">Still Enrolled</TableCell>
                  <TableCell align="right">On Track</TableCell>
                  <TableCell align="right">Repeating</TableCell>
                  <TableCell align="right">Dropped Out</TableCell>
                  <TableCell align="right">Attrition</TableCell>
                  <TableCell align="right">Survival %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grade_survival.map((g) => (
                  <TableRow key={g.year} hover>
                    <TableCell>{g.year}</TableCell>
                    <TableCell><Typography fontWeight="bold">{g.grade_label}</Typography></TableCell>
                    <TableCell align="right">{g.cohort_enrolled.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: '#388e3c' }}>
                      {g.on_track.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#f57c00' }}>
                      {g.repeating.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#d32f2f' }}>
                      {g.dropped_out.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {g.attrition_this_year > 0 ? (
                        <Chip label={`-${g.attrition_this_year}`} size="small" color="error" variant="outlined" />
                      ) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${g.survival_rate}%`}
                        size="small"
                        color={g.survival_rate >= 80 ? 'success' : g.survival_rate >= 60 ? 'warning' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* District Comparison */}
      {district_comparison.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Survival Rate by District</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={district_comparison} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} unit="%" />
                    <YAxis dataKey="district_name" type="category" width={100} />
                    <Tooltip formatter={(value: number) => `${value}%`} />
                    <ReferenceLine x={80} stroke="#388e3c" strokeDasharray="3 3" />
                    <Bar dataKey="survival_rate" fill="#1976d2" name="Survival Rate" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item xs={12} md={5}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>District</TableCell>
                        <TableCell align="right">Cohort</TableCell>
                        <TableCell align="right">Surviving</TableCell>
                        <TableCell align="right">Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {district_comparison.map((d) => (
                        <TableRow key={d.district_name} hover>
                          <TableCell><Typography fontWeight="bold">{d.district_name}</Typography></TableCell>
                          <TableCell align="right">{d.cohort_size.toLocaleString()}</TableCell>
                          <TableCell align="right">{d.still_enrolled.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Chip label={`${d.survival_rate}%`} size="small"
                              color={d.survival_rate >= 80 ? 'success' : d.survival_rate >= 60 ? 'warning' : 'error'}
                              variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CohortSurvivalTab;
